import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    OnDestroy,
    ViewChild,
    computed,
    effect,
    inject,
    input,
    linkedSignal,
    output,
    signal,
    untracked
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';

import {
    ActionItem,
    CheckboxSelectionFormValues,
    ColumnDef as SortableColumnDef,
    FormSelectOption,
    FormValue,
    FormValueState,
    QueryPartData,
    ShColumnFilterField,
    ShFacet,
    IconSize,
    ShSelectedFacetValue,
    SortDirection,
    SortInfo
} from '@stylehaven/components';
import { Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';

import { Column } from '../../entities/column.entity';
import { HierarchyRow, RowKind } from '../../entities/hierarchy-row.entity';
import { Comparable } from '../../entities/misc/comparable.interface';
import { Trackable } from '../../entities/misc/trackable.interface';
import {
    getColumnEnumFacet,
    getColumnFilterValue,
    getColumnSelectedEnumValues,
    hasColumnFilter,
    toColumnFilterFields,
    toSortColumnDef
} from '../../helpers/column-header-controls.helper';
import { toSpacedPathCaption } from '../../helpers/path-caption.helper';

/** Shared so a column with no declared filter fields hands the popover a stable reference, not a fresh array. */
const NO_FILTER_FIELDS: ShColumnFilterField[] = [];

export const HIERARCHY_TABLE_SCROLL_REF =
    '.dgh-overview-page .sh-hierarchy-table-stack__grid > .sh-hierarchy-table-wrapper';

/** Where a row sits among its own siblings: 1-based, and `size` counts the ones still to load with it. */
interface RowPosition {
    readonly position: number;
    readonly size: number;
}

/** What the live region says about an expansion: a translation key, and the number of stukken it loaded. */
export interface ExpansionAnnouncement {
    readonly key: string;
    readonly count: number;
    readonly params?: Record<string, string | number>;
}

/** Where the fixed selection bar has to be drawn to sit over the table's own column and nothing else. */
interface SelectionBarBounds {
    readonly left: number;
    readonly width: number;
}

/** All the header checkbox's compound value means to this table: tick the page, and take the whole result set. */
interface SelectAllIntent {
    readonly checked: boolean;
    readonly batch: boolean;
}

/**
 * The v2 overview table (HFBD-7a): the same rows as `digihaven-list`, laid out on a CSS Grid instead of a
 * `<table>` so that a later ticket can give each hierarchy level its own column widths — something
 * `table-layout: fixed` forbids. `digihaven-list` is left untouched; the container picks between them.
 *
 * Every row is its own grid rather than one grid with `subgrid` rows, because the levels must be able to
 * disagree about the tracks in the middle. The leading and trailing tracks are fixed widths, which is what
 * keeps both outer edges lined up between levels.
 */
@Component({
    standalone: false,
    selector: 'digihaven-hierarchical-list',
    templateUrl: './hierarchical-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HierarchicalListComponent<T extends Comparable & Trackable> implements AfterViewChecked, OnDestroy {
    /**
     * The chevron slot. Just wide enough for the caret's hit box — it draws no surface of its own, so it needs
     * no room around it for one, and the glyph inside still reads with air on both sides.
     */
    static readonly CHEVRON_TRACK = '1.5rem';

    static readonly MAX_INDENT_STEPS: number = 2;

    static readonly INDENT_VAR = 'var(--sh-hierarchy-indent, 0px)';

    static readonly INDENTED_CHEVRON_TRACK =
        `calc(${HierarchicalListComponent.CHEVRON_TRACK} + ${HierarchicalListComponent.INDENT_VAR})`;
    /**
     * The checkbox slot. One width at every level; what moves it is the indent in the track before it.
     *
     * Its width is set by the header's *dropdown* checkbox, which is a 20px box with a 16px chevron after it.
     * The chevron cannot hang over the neighbouring column (no `overflow` can hide it: the cell keeps
     * `overflow: visible` for its scroll shadow) and cannot leave the control either (it is
     * `pointer-events: none`, so a click on the arrow only opens the menu while it overlays the toggle).
     *
     * So the box sits one chevron's width in, which both centres it — 12px of track either side — and leaves the
     * chevron exactly the last 12px. The rows' bare inputs take the same inset, so the two are aligned *and*
     * centred, which is what a sticky column standing on its own has to be. The 12px is
     * `$sh-hierarchy-table-select-chevron-size`, and this is `20px + 2 ×` it: they have to agree or the box is
     * off-centre and the chevron overhangs.
     */
    static readonly LEADING_TRACK = '2.75rem';
    /** Wide enough for the `Acties` header label, which is broader than the `⋯` button it sits above. */
    static readonly ACTIONS_TRACK = '4rem';

    readonly rows = input<HierarchyRow<T>[] | null>(null);
    readonly columns = input<Column<T>[] | null>(null);
    /**
     * The stuk level's own column set. Optional: a page that configures no second level falls back to the
     * dossier columns, which is what every child row rendered before per-level sets existed.
     */
    readonly childColumns = input<Column<T>[] | null>(null);
    readonly isLoading = input<boolean | null>(false);
    readonly hasError = input<boolean | null>(false);
    readonly enableSelect = input<boolean | null>(false);
    readonly hasRowActions = input<boolean | null>(false);
    readonly overviewId = input<string | undefined>(undefined);

    /**
     * Whether a `Type` filter has dropped the table back to a flat list. It changes what the table *claims to
     * be*: a flattened one has no tree, so promising treegrid navigation would promise something that has
     * nothing to navigate. Fed in rather than derived from the rows, so the semantics answer to the same state
     * the rest of flat mode does — a page whose rows all happen to be childless is still a tree.
     */
    readonly isFlattened = input<boolean | null>(false);

    readonly emptyMessageKey = input<string>('ui.no-results');
    readonly errorMessageKey = input<string>('ui.errors.unknown');

    /**
     * Kolombeheer's stuk-level "Toon submappen" preference. Off ties a level-1 row's own path caption to the
     * same fate as its submap header: {@link groupBySubmap} already withholds the header when this is off, and
     * this is what stops the row falling back to captioning itself in the header's place. A level-0 row's
     * caption — the *flattened* tree's own, unrelated fallback (HFBD-85) — is untouched, which is why this
     * gates on {@link HierarchyRow.level} rather than replacing {@link HierarchyRow.grouped} outright.
     */
    readonly showSubfolders = input<boolean | null>(true);

    /**
     * The `⋯` menu per row, keyed by `HierarchyRow.key` rather than by position — the flat list can index
     * into a parallel array because its rows never interleave, but child rows will.
     */
    readonly rowActions = input<ReadonlyMap<string, Observable<ActionItem>> | null>(null);

    /** The bulk actions offered for the rows the user ticked, and for the whole result set respectively. */
    readonly actionBarActionItems = input<Observable<ActionItem>[] | null>(null);
    readonly batchActionItems = input<ActionItem[] | null>(null);
    /** What the batch bar counts: the whole result set, not the page. */
    readonly totalNumberOfResults = input<number | null>(null);

    readonly currentSort = input<SortInfo | null>({ field: '', direction: SortDirection.ASC });
    readonly activeColumnFilters = input<QueryPartData[] | null>([]);
    readonly activeFacetFilters = input<QueryPartData[] | null>([]);
    readonly enumFacets = input<ReadonlyMap<string, ShFacet> | null>(null);
    readonly enumFacetFieldKeys = input<string[] | null>([]);

    readonly rowNavigation = output<T>();
    readonly recordInteraction = output<T>();
    /** The chevron owns expansion outright — the title link still navigates and nothing else changes. */
    readonly expandToggled = output<HierarchyRow<T>>();
    /**
     * The parent's key: fetch the rest of its children. Both "Toon meer stukken" and a retry after a failed
     * batch mean the same request — resume this expansion from wherever it got to.
     */
    readonly moreChildrenRequested = output<string>();
    readonly collapseAllRequested = output<void>();
    readonly selectionChanged = output<Observable<T>[]>();
    readonly sortChanged = output<SortInfo>();
    readonly columnFilterChanged = output<{ field: string; value: string }>();
    readonly enumFilterOpened = output<string>();
    readonly facetValueSelected = output<ShSelectedFacetValue>();
    readonly enumFilterCleared = output<string>();

    /** The checkbox column sticks behind the chevron column, so its offset is that track's width — or none. */
    readonly leadingTrack = computed<string>(() =>
        this.showsChevronTrack() ? HierarchicalListComponent.INDENTED_CHEVRON_TRACK : '0'
    );

    readonly iconSize = IconSize;

    /**
     * Whether the first track is drawn at all. It holds two things — a row's chevron, and the trunk a submap
     * header opens — so a flattened table with no groups in it has no use for the width and gives it back to the
     * columns. Kept for the whole of tree mode rather than asked of the rows: a page whose rows happen to be
     * childless is still a tree, and the columns should not shift as the user pages through one.
     */
    readonly showsChevronTrack = computed<boolean>(
        () =>
            !this.isFlattened() ||
            (this.rows() ?? []).some(row => row.kind === 'submap' || this.isInSubmapGroup(row))
    );

    /** The fixed tracks before the data columns, each present only when something needs it. */
    private readonly leadingTracks = computed<string[]>(() => [
        ...(this.showsChevronTrack() ? [HierarchicalListComponent.INDENTED_CHEVRON_TRACK] : []),
        ...(this.enableSelect() ? [HierarchicalListComponent.LEADING_TRACK] : [])
    ]);

    readonly gridTemplate = computed<string>(() => this.toGridTemplate(this.columns() ?? []));

    /** The stuk level's tracks. Only the middle differs — the shared leading and trailing ones anchor both
     *  outer edges, which is what lets two disagreeing levels still read as one table. */
    readonly childGridTemplate = computed<string>(() => this.toGridTemplate(this.resolvedChildColumns()));

    private readonly resolvedChildColumns = computed<Column<T>[]>(
        () => this.childColumns() ?? this.columns() ?? []
    );

    /**
     * A data column's declared width becomes both the track's floor and its share of any spare space — the
     * same proportional-with-a-floor sizing `table-layout: fixed` gave the old table. The organism sizes the
     * table under a min-content constraint so those floors, not the cell contents, decide when it scrolls.
     *
     * The **lead** data column is the exception: it is fixed at its declared width, because it is the one
     * column both levels have to agree on. Left flexible it would take a share of the spare space proportional
     * to its own level's total, and the two levels declare different totals — so a stuk's name would land at a
     * different width from its dossier's on any viewport wider than the table's floor.
     * A lone column has no other level to disagree with, and takes the spare space as its own floor.
     */
    private toGridTemplate(columns: Column<T>[]): string {
        const tracks: string[] = [...this.leadingTracks()];
        const leadIndex: number = this.leadColumnIndex(columns);

        columns.forEach((column, index) => {
            const width: number = column.columnDefinition.width;
            const floor: string = `calc(${width}px - ${HierarchicalListComponent.INDENT_VAR})`;

            if (index < leadIndex) {
                tracks.push(`${width}px`);
            } else if (index > leadIndex) {
                tracks.push(`minmax(${width}px, ${width}fr)`);
            } else if (leadIndex > 0) {
                tracks.push(`minmax(${floor}, ${width}fr)`);
            } else {
                tracks.push(columns.length === 1 ? `minmax(${floor}, 1fr)` : floor);
            }
        });

        if (this.hasRowActions()) {
            tracks.push(HierarchicalListComponent.ACTIONS_TRACK);
        }

        return tracks.join(' ');
    }

    /**
     * The path caption's text, which is the path with a little air around its separators. Typesetting only — the
     * row carries the path itself, and that is what the hover title, a copied selection and a screen reader get.
     */
    spacedPath(path: string | null | undefined): string {
        return path ? toSpacedPathCaption(path) : '';
    }

    /**
     * Whether a submap header stands above this row — which is what the connector is drawn from, and not the
     * same question as {@link HierarchyRow.grouped}: a stuk at the dossier root is grouped and headed by
     * nothing, so tying it to a trunk would point at a folder that does not exist.
     */
    isInSubmapGroup(row: HierarchyRow<T>): boolean {
        return !!row.grouped && !!row.submap;
    }

    /**
     * Whether a record row's own path caption should stay visually hidden — either because a submap header
     * already carries it ({@link HierarchyRow.grouped}), or because the stuk level's "Toon submappen" checkbox
     * is off, in which case there is no header for it to fall back to and it stays hidden regardless.
     */
    isPathCaptionHidden(row: HierarchyRow<T>): boolean {
        return !!row.grouped || (row.level > 0 && this.showSubfolders() === false);
    }

    /** Which level's columns a row renders. Every level below the first shares the stuk set. */
    columnsFor(row: HierarchyRow<T>): Column<T>[] {
        return row.level > 0 ? this.resolvedChildColumns() : (this.columns() ?? []);
    }

    private leadColumnIndex(columns: Column<T>[]): number {
        return Math.max(0, columns.findIndex(column => column.columnDefinition.showHeader));
    }

    isLeadCell(row: HierarchyRow<T>, index: number): boolean {
        return index === this.leadColumnIndex(this.columnsFor(row));
    }

    gridTemplateFor(row: HierarchyRow<T>): string {
        return row.level > 0 ? this.childGridTemplate() : this.gridTemplate();
    }

    indentSteps(row: HierarchyRow<T>): number | null {
        return row.level > 0 ? Math.min(row.level, HierarchicalListComponent.MAX_INDENT_STEPS) : null;
    }

    /**
     * "Alles inklappen" appears only once something is open. There is deliberately no expand-all: it would
     * fire a child request per row on the page and could render hundreds of rows at once.
     */
    readonly anyExpanded = computed<boolean>(() => (this.rows() ?? []).some(row => !!row.expanded));

    /**
     * A structural row (skeleton, "Toon meer", an inline message, a submap header) carries one cell that runs
     * from the first data column to the trailing edge — the leading tracks keep cells of their own so the
     * chevron and checkbox columns, and the scroll shadows they carry, line up with the rows around it. Derived
     * from the same track list the grid is built from, so a third leading track could never silently shift the
     * span.
     */
    readonly structuralSpan = computed<string>(() => `${this.leadingTracks().length + 1} / -1`);

    /**
     * The submap header's caption, which starts one track in rather than after all the leading ones: it lines up
     * with the checkbox column, not with the names, and the chevron track it leaves out is where its connector is
     * drawn. Fixed rather than derived from `leadingTracks`, because that one track is the whole point.
     */
    readonly submapSpan: string = '2 / -1';

    readonly showRows = computed<boolean>(() => this.isIdle() && (this.rows()?.length ?? 0) > 0);

    /** `null` rows mean no page has arrived yet, which is not the same as a page that came back empty. */
    readonly showEmptyState = computed<boolean>(() => this.isIdle() && this.rows()?.length === 0);

    private isIdle(): boolean {
        return !this.isLoading() && !this.hasError();
    }

    // #region Selection
    /**
     * Keyed by `HierarchyRow.key` — the record id — and holding each ticked row's own stream, so the
     * selection outlives the rows it was made on rather than shrinking on every page, sort or collapse.
     */
    private readonly selectedRecordsByKey = signal<ReadonlyMap<string, Observable<T>>>(
        new Map<string, Observable<T>>()
    );

    /** Whether the user asked for the whole result set rather than the page: shows the second action bar. */
    private readonly batchSelected = signal(false);

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    /** Structural rows carry no record, and stuk rows are deliberately not selectable — see the spec's §9. */
    readonly selectableRows = computed<HierarchyRow<T>[]>(() =>
        (this.rows() ?? []).filter(row => this.isRowSelectable(row))
    );

    readonly selectedRecords = computed<Observable<T>[]>(() => [...this.selectedRecordsByKey().values()]);

    readonly selectedCount = computed<number>(() => this.selectedRecordsByKey().size);

    /** Page-scoped, unlike the count: the header checkbox speaks for the rows under it, not for the selection. */
    readonly allSelected = computed<boolean>(() => {
        const selected: ReadonlyMap<string, Observable<T>> = this.selectedRecordsByKey();
        const rows: HierarchyRow<T>[] = this.selectableRows();
        return rows.length > 0 && rows.every(row => selected.has(row.key));
    });

    readonly someSelected = computed<boolean>(() => this.selectedCount() > 0 && !this.allSelected());

    /**
     * One bar, in one of two modes. Asking for the whole result set does not add a second bar beside the page's
     * — it re-aims the one that is already there, because both answer the same question (what is selected, and
     * what can be done with it) and two of them stacked only made the reader compare two counts.
     */
    readonly showPageSelectionBar = computed<boolean>(() => !!this.enableSelect() && this.selectedCount() > 0);
    readonly showBatchSelectionBar = computed<boolean>(() => !!this.enableSelect() && this.batchSelected());
    readonly showSelectionBar = computed<boolean>(() => this.showPageSelectionBar() || this.showBatchSelectionBar());

    /** In batch mode the count *is* the total, so the bar never reads as a partial selection of itself. */
    readonly selectionBarCount = computed<number>(() =>
        this.showBatchSelectionBar() ? (this.totalNumberOfResults() ?? 0) : this.selectedCount()
    );

    readonly selectionBarTotal = computed<number>(() =>
        this.showBatchSelectionBar() ? (this.totalNumberOfResults() ?? 0) : this.selectableRows().length
    );

    readonly selectionBarActionItems = computed<Observable<ActionItem>[]>(() =>
        this.showBatchSelectionBar() ? this.batchActionItemObservables() : (this.actionBarActionItems() ?? [])
    );

    /** The action bar takes streams; the batch actions are plain items, already resolved against the query. */
    readonly batchActionItemObservables = computed<Observable<ActionItem>[]>(() =>
        (this.batchActionItems() ?? []).map(item => of(item))
    );

    /**
     * The header checkbox only needs a form control in its dropdown variant, which is a vendor control-value
     * accessor. The plain variant is a bare input bound to the signals above.
     */
    readonly selectAllCheckbox = new FormControl<CheckboxSelectionFormValues | null>(null);

    readonly selectAllOptions: FormSelectOption[] = [
        {
            label: 'Het volledig zoekresultaat selecteren',
            value: 'batch'
        }
    ];

    /**
     * What the checkbox last said, as the two booleans that actually drive selection. The accessor echoes every
     * write to `selectAllCheckbox` back out through `valueChanges`, and it does so a tick later — so a flag held
     * around the write cannot catch it, and the sync would answer itself for as long as the stack allowed.
     * Comparing values instead converges, because {@link syncSelectAllControl} can predict its own echo.
     */
    private lastSelectAllIntent: SelectAllIntent = { checked: false, batch: false };

    /**
     * Set while this table has withdrawn the whole-result-set request itself. The accessor's *selection* half
     * lags its checkbox half by a tick — it is the dropdown panel's own state, and the panel is told through an
     * async input — so until it catches up, an incoming value still claiming the batch is a stale echo. Acting
     * on one re-armed exactly what the user had just cancelled, and the two then wrote over each other.
     */
    private batchRequestWithdrawn = false;

    constructor() {
        this.selectAllCheckbox.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe(value => this.onSelectAllValueChanged(value));

        effect(() => {
            const rows: HierarchyRow<T>[] = this.selectableRows();
            untracked(() => this.selectRowsShownInBatch(rows));
        });
    }

    isRowSelectable(row: HierarchyRow<T>): boolean {
        return row.kind === 'record' && row.selectable && !!row.record$;
    }

    /**
     * Gated on selectability as well as on the key, so a row that cannot be ticked can never carry the
     * selected tint either — §9 asks for both, and only one of them has a checkbox to make it obvious.
     */
    isRowSelected(row: HierarchyRow<T>): boolean {
        return this.isRowSelectable(row) && this.selectedRecordsByKey().has(row.key);
    }

    selectRow(row: HierarchyRow<T>, record: T, event: Event): void {
        const isChecked: boolean = (event.target as HTMLInputElement).checked;
        const selected: Map<string, Observable<T>> = new Map(this.selectedRecordsByKey());

        if (isChecked) {
            selected.set(row.key, row.record$!);
        } else {
            selected.delete(row.key);
            // Whatever is left is no longer "the whole result set", so that claim is dropped — otherwise the
            // bar would keep counting the total and the untick would look like it did nothing.
            this.withdrawBatchRequest();
        }

        this.selectedRecordsByKey.set(selected);
        this.syncSelectAllControl();

        // Ticking a row is what triggers the container to fetch it in full, which the bulk actions need.
        if (isChecked) {
            this.recordInteraction.emit(record);
        }

        this.selectionChanged.emit(this.selectedRecords());
    }

    onSelectAllToggled(event: Event): void {
        this.toggleSelectAll((event.target as HTMLInputElement).checked);
    }

    /** Drops "the whole result set" without touching the ticked rows, which survive a new search. */
    clearBatchSelection(): void {
        if (!this.batchSelected()) {
            return;
        }

        this.withdrawBatchRequest();
        this.syncSelectAllControl();
    }

    /** Selecting adds this page; deselecting clears every page's worth. The container clears here too. */
    toggleSelectAll(selectAll: boolean): void {
        if (!selectAll && this.selectedCount() === 0 && !this.batchSelected()) {
            return;
        }

        if (!selectAll) {
            this.withdrawBatchRequest();
        }

        const rows: HierarchyRow<T>[] = this.selectableRows();
        const selected: Map<string, Observable<T>> = selectAll
            ? new Map(this.selectedRecordsByKey())
            : new Map<string, Observable<T>>();

        if (selectAll) {
            rows.forEach(row => selected.set(row.key, row.record$!));
        }

        this.selectedRecordsByKey.set(selected);
        this.syncSelectAllControl();

        if (selectAll) {
            this.emitInteractionForRows(rows);
        }

        this.selectionChanged.emit(this.selectedRecords());
    }

    private selectRowsShownInBatch(rows: HierarchyRow<T>[]): void {
        if (!this.batchSelected()) {
            return;
        }

        const selected: Map<string, Observable<T>> = new Map(this.selectedRecordsByKey());
        const added: HierarchyRow<T>[] = rows.filter(row => !selected.has(row.key));

        if (added.length === 0) {
            return;
        }

        added.forEach(row => selected.set(row.key, row.record$!));
        this.selectedRecordsByKey.set(selected);
        this.syncSelectAllControl();
        this.emitInteractionForRows(added);
        this.selectionChanged.emit(this.selectedRecords());
    }

    /** Drops the whole-result-set claim, and remembers that the accessor has not been told yet. */
    private withdrawBatchRequest(): void {
        if (!this.batchSelected()) {
            return;
        }

        this.batchSelected.set(false);
        this.batchRequestWithdrawn = true;
    }

    private onSelectAllValueChanged(value: CheckboxSelectionFormValues | null): void {
        const intent: SelectAllIntent = {
            batch: !!value?.selection?.some(item => item.value === 'batch' && item.state === FormValueState.SET),
            checked: value?.checkbox?.state === FormValueState.SET && !!value.checkbox.value
        };

        if (this.batchRequestWithdrawn) {
            if (intent.batch) {
                return;
            }

            this.batchRequestWithdrawn = false;
        }

        if (intent.checked === this.lastSelectAllIntent.checked && intent.batch === this.lastSelectAllIntent.batch) {
            return;
        }

        const untickedByHand: boolean = this.lastSelectAllIntent.checked && !intent.checked;
        this.lastSelectAllIntent = intent;

        if (untickedByHand) {
            // Unticking the header means "none", so it withdraws the request for the whole result set as well.
            // Without that the standing `batch` would re-select the page from under the click, and the count
            // could never come back down to zero. `toggleSelectAll` drops the flag itself.
            this.toggleSelectAll(false);
            return;
        }

        this.batchSelected.set(intent.batch);
        // Asking for the whole result set implies the page, exactly as the flat list does.
        this.toggleSelectAll(intent.checked || intent.batch);
    }

    private syncSelectAllControl(): void {
        const current: CheckboxSelectionFormValues | null = this.selectAllCheckbox.value;

        if (!current) {
            return;
        }

        const state: FormValueState = this.allSelected()
            ? FormValueState.SET
            : this.someSelected()
              ? FormValueState.INDETERMINATE
              : FormValueState.UNSET;

        // Recorded before the write, not after: the accessor echoes synchronously from inside `setValue` as well
        // as a tick later. The prediction holds for all three states — an indeterminate one comes back reported
        // as simply unticked, which is what `allSelected()` says too.
        this.lastSelectAllIntent = { checked: this.allSelected(), batch: this.batchSelected() };

        this.selectAllCheckbox.setValue(
            {
                checkbox: new FormValue<boolean>(this.allSelected(), state),
                selection: current.selection.map(
                    item => new FormValue(item.value, this.batchSelected() ? FormValueState.SET : FormValueState.UNSET)
                )
            },
            { emitEvent: false }
        );
    }

    private emitInteractionForRows(rows: HierarchyRow<T>[]): void {
        for (const row of rows) {
            row.record$!.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(record =>
                this.recordInteraction.emit(record)
            );
        }
    }
    // #endregion

    // #region Accessibility and keyboard navigation
    /**
     * Tree mode is a `treegrid` — the role an expandable grid actually has — and flat mode degrades to a plain
     * `table`, which is what the `<table>` this replaced announced.
     */
    readonly isTree = computed<boolean>(() => !this.isFlattened());
    readonly gridRole = computed<'treegrid' | 'table'>(() => (this.isTree() ? 'treegrid' : 'table'));

    /** A `grid`'s cells are `gridcell`s and a `table`'s are `cell`s; the wrong one is a silent downgrade. */
    readonly cellRole = computed<'gridcell' | 'cell'>(() => (this.isTree() ? 'gridcell' : 'cell'));

    /**
     * `-1` is the standard "not known", and it genuinely is not: children load on demand, so how many rows the
     * grid will end up holding is undecided while the user is still opening dossiers. Flat mode has every row
     * it will ever have, so it declares nothing rather than declaring ignorance.
     *
     * No `aria-rowindex` goes with it: that is for grids that leave rows out of the DOM, and this one renders
     * every row it has.
     */
    readonly ariaRowcount = computed<string | null>(() => (this.isTree() ? '-1' : null));

    /** The row kinds that exist for the eye alone, so neither a screen reader nor an arrow key visits them. */
    private static readonly DECORATIVE_KINDS: ReadonlySet<RowKind> = new Set<RowKind>([
        'subheader',
        'submap',
        'skeleton'
    ]);

    /**
     * Hidden from assistive technology, and skipped by the keyboard with it. The sub-header labels the columns
     * of the rows below it, which those rows now carry themselves ({@link isNamedCellRow}); a skeleton stands
     * in for a row that is not there yet, and its parent says {@link ariaBusy} instead; and a submap header
     * says once what each of the rows below it still says for itself, in a visually-hidden label.
     *
     * That last one is why grouping changes nothing a screen reader hears — and, because
     * {@link navigableRows} filters on this, why it adds no arrow-key stops either.
     */
    ariaHidden(row: HierarchyRow<T>): true | null {
        return HierarchicalListComponent.DECORATIVE_KINDS.has(row.kind) ? true : null;
    }

    /** The row model counts levels from 0; ARIA counts them from 1. */
    ariaLevel(row: HierarchyRow<T>): number | null {
        return this.isTree() ? row.level + 1 : null;
    }

    /**
     * On the row rather than on the chevron button: the row-focus variant of the pattern puts the state on the
     * thing that takes focus, and a button that announced it too would say it twice.
     */
    ariaExpanded(row: HierarchyRow<T>): boolean | null {
        return this.isTree() && row.expandable ? !!row.expanded : null;
    }

    /** The dossier whose children are on their way — the skeletons standing in for them are hidden. */
    ariaBusy(row: HierarchyRow<T>): true | null {
        return this.busyParentKeys().has(row.key) ? true : null;
    }

    private readonly busyParentKeys = computed<ReadonlySet<string>>(
        () =>
            new Set(
                (this.rows() ?? [])
                    .filter(row => row.kind === 'skeleton' && !!row.parentKey)
                    .map(row => row.parentKey!)
            )
    );

    /**
     * Where the row sits among its own siblings. The top level's set is the page; a stuk's is its dossier's
     * children, whose size is **loaded plus still to come** — the "Toon meer" row knows the remainder, so a
     * half-loaded expansion can still say "1 van 12" rather than lying about there being three.
     *
     * Only the row kinds that are items of the tree take part: a sub-header, a skeleton and an inline message
     * are not siblings of the stukken they sit among, and counting them would put the numbers out.
     */
    private readonly rowPositions = computed<readonly (RowPosition | null)[]>(() => {
        const rows: HierarchyRow<T>[] = this.rows() ?? [];
        const positions: (RowPosition | null)[] = rows.map(() => null);

        if (!this.isTree()) {
            return positions;
        }

        // By row index, not by row key: two rows under one key would otherwise report the same place in the set.
        const siblings = new Map<string | null, number[]>();
        const stillToCome = new Map<string | null, number>();

        rows.forEach((row, index) => {
            if (row.kind === 'show-more') {
                stillToCome.set(row.parentKey, row.remaining ?? 0);
            }

            if (!this.isTreeItem(row)) {
                return;
            }

            const parentKey: string | null = row.level === 0 ? null : row.parentKey;
            const group: number[] = siblings.get(parentKey) ?? [];

            group.push(index);
            siblings.set(parentKey, group);
        });

        for (const [parentKey, group] of siblings) {
            const size: number = group.length + (stillToCome.get(parentKey) ?? 0);

            group.forEach((rowIndex, position) => (positions[rowIndex] = { position: position + 1, size }));
        }

        return positions;
    });

    ariaPosinset(index: number): number | null {
        return this.rowPositions()[index]?.position ?? null;
    }

    ariaSetsize(index: number): number | null {
        return this.rowPositions()[index]?.size ?? null;
    }

    /** The rows that are items of the tree, as opposed to the structure the table draws around them. */
    private isTreeItem(row: HierarchyRow<T>): boolean {
        return row.kind === 'record' || row.kind === 'placeholder';
    }

    /**
     * A stuk-level row names its own cells. The sub-header that carries those labels visually is hidden, and
     * the header row that is not hidden governs the *dossier* level's tracks — which are different columns in
     * different widths, so associating them with a stuk's cells by position would mislabel every one of them.
     * Two header rows in one grid is also exactly where screen readers disagree; a cell that names itself does
     * not depend on any of that.
     */
    isNamedCellRow(row: HierarchyRow<T>): boolean {
        return row.level > 0;
    }

    /**
     * The rows the arrow keys move between: everything the screen reader can see, in the order it sees it.
     * Collapsed subtrees are absent from `rows()` altogether, so "visible" needs no further filtering. Flat
     * mode has no roving tab stop — a plain table is not a widget.
     */
    private readonly navigableRows = computed<HierarchyRow<T>[]>(() =>
        this.isTree() ? (this.rows() ?? []).filter(row => !this.ariaHidden(row)) : []
    );

    /**
     * Which row is the grid's single tab stop. Follows the rows: the key survives a re-render, and only falls
     * back to the first row once the row it named is gone — collapsing the dossier you were standing in, for
     * instance.
     */
    private readonly focusedRowKey = linkedSignal<HierarchyRow<T>[], string | null>({
        source: this.navigableRows,
        computation: (rows, previous) => {
            const keys: string[] = rows.map(row => row.key);

            return previous?.value && keys.includes(previous.value) ? previous.value : (keys[0] ?? null);
        }
    });

    /**
     * Which row in `rows()` is the tab stop. By position rather than by key match, so that "exactly one" holds
     * even when two rows arrive under the same key — which real data should never do, but the offline mock
     * does, and a table with 23 tab stops is not a treegrid.
     */
    private readonly tabStopIndex = computed<number>(() =>
        (this.rows() ?? []).findIndex(row => row.key === this.focusedRowKey() && !this.ariaHidden(row))
    );

    /**
     * A roving tabindex: one row is reachable by Tab and the arrow keys move which one. The controls inside a
     * row keep their own place in the tab order, which is what the row-focus variant of the pattern buys —
     * cell focus would have made the whole table a single tab stop.
     */
    rowTabIndex(row: HierarchyRow<T>, index: number): number | null {
        if (!this.isTree() || this.ariaHidden(row)) {
            return null;
        }

        return index === this.tabStopIndex() ? 0 : -1;
    }

    /** Clicking a row, or tabbing into one of its controls, moves the tab stop to it. */
    onRowFocusIn(row: HierarchyRow<T>): void {
        if (this.isTree() && !this.ariaHidden(row)) {
            this.focusedRowKey.set(row.key);
        }
    }

    onRowKeydown(event: KeyboardEvent, row: HierarchyRow<T>): void {
        // Only while the row itself holds focus: the checkbox, the title link and the actions menu inside it
        // own their own keys, and an actions menu in particular navigates with the same arrows.
        if (!this.isTree() || event.target !== event.currentTarget) {
            return;
        }

        switch (event.key) {
            case 'ArrowDown':
                this.moveRowFocus(row, 1);
                break;
            case 'ArrowUp':
                this.moveRowFocus(row, -1);
                break;
            case 'Home':
                this.focusRowAt(0);
                break;
            case 'End':
                this.focusRowAt(this.navigableRows().length - 1);
                break;
            case 'ArrowRight':
                this.onExpandKey(row);
                break;
            case 'ArrowLeft':
                this.onCollapseKey(row);
                break;
            default:
                return;
        }

        // Only for the keys handled above, so Tab, the space bar and type-ahead still do what they always do.
        event.preventDefault();
    }

    /** Opens a closed dossier and steps into an open one. The same toggle the chevron owns by mouse. */
    private onExpandKey(row: HierarchyRow<T>): void {
        if (row.expandable && !row.expanded) {
            this.expandToggled.emit(row);
            return;
        }

        const firstChild: HierarchyRow<T> | undefined = this.navigableRows().find(
            candidate => candidate.parentKey === row.key
        );

        if (firstChild) {
            this.focusRow(firstChild.key);
        }
    }

    /** Closes an open dossier, and otherwise climbs out of the one this row is in. */
    private onCollapseKey(row: HierarchyRow<T>): void {
        if (row.expandable && row.expanded) {
            this.expandToggled.emit(row);
            return;
        }

        if (row.parentKey) {
            this.focusRow(row.parentKey);
        }
    }

    /**
     * By key, which is what the row model promises is unique. Where it is not — the offline mock serves 25 rows
     * under 3 ids — an arrow key can land back on the first row carrying that id instead of advancing. Angular's
     * own `@for` is already logging `NG0955` about those rows; the repair is unique ids, not this.
     */
    private moveRowFocus(row: HierarchyRow<T>, delta: number): void {
        const index: number = this.navigableRows().findIndex(candidate => candidate.key === row.key);

        if (index >= 0) {
            this.focusRowAt(index + delta);
        }
    }

    /** Out of range is a no-op: the first and last rows are where the arrow keys stop. */
    private focusRowAt(index: number): void {
        const row: HierarchyRow<T> | undefined = this.navigableRows()[index];

        if (row) {
            this.focusRow(row.key);
        }
    }

    /**
     * Moves the tab stop and the browser's focus together. The element is found by its key rather than by its
     * index, because the two only agree while every row renders — and a row renders nothing at all until the
     * record stream behind it has emitted.
     */
    private focusRow(key: string): void {
        this.focusedRowKey.set(key);
        this.rowElementFor(key)?.focus();
    }

    private rowElementFor(key: string): HTMLElement | null {
        const elements: HTMLElement[] = Array.from(
            this.grid?.nativeElement.querySelectorAll<HTMLElement>('[data-row-key]') ?? []
        );

        return elements.find(element => element.dataset['rowKey'] === key) ?? null;
    }

    /**
     * What each settled expansion has to say for itself, keyed by the dossier that was opened. An expansion
     * still loading says nothing — its parent row is marked busy, and the outcome follows once it is known.
     */
    private readonly expansionOutcomes = computed<ReadonlyMap<string, ExpansionAnnouncement>>(() => {
        const rows: HierarchyRow<T>[] = this.rows() ?? [];
        const outcomes = new Map<string, ExpansionAnnouncement>();

        for (const parent of rows.filter(row => row.expanded)) {
            const children: HierarchyRow<T>[] = rows.filter(row => row.parentKey === parent.key);

            if (children.some(child => child.kind === 'skeleton')) {
                continue;
            }

            const loaded: number = children.filter(child => child.kind === 'record').length;

            if (loaded > 0) {
                outcomes.set(parent.key, { key: 'hierarchy.loaded', count: loaded });
                continue;
            }

            const message: HierarchyRow<T> | undefined = children.find(child => child.kind === 'message');

            if (message?.messageKey) {
                outcomes.set(parent.key, { key: message.messageKey, count: 0, params: message.messageParams });
            }
        }

        return outcomes;
    });

    /**
     * The outcome of the expansion that has just settled — the rows appear after the keypress that asked for
     * them, so nothing else tells a screen reader user what happened.
     *
     * A `linkedSignal` rather than an `effect`: announcing needs the *previous* state to know which expansion
     * is the new one, which is exactly what its computation is handed, and it stays a pure derivation — the
     * region announces because its text changed, not because anything pushed to it. `null` on the first pass
     * for the same reason: a tree restored from the store on arrival did not just happen.
     *
     * A pass with nothing new **keeps** the last outcome rather than clearing it: rows can be rebuilt again
     * within the moment it takes to read, and an emptied region swallows what it was about to say. The cost is
     * that an outcome identical to the one still standing there is not read a second time.
     */
    readonly expansionAnnouncement = linkedSignal<
        ReadonlyMap<string, ExpansionAnnouncement>,
        ExpansionAnnouncement | null
    >({
        source: this.expansionOutcomes,
        computation: (outcomes, previous) => {
            if (!previous) {
                return null;
            }

            for (const [parentKey, outcome] of outcomes) {
                const before: ExpansionAnnouncement | undefined = previous.source.get(parentKey);

                if (!before || before.key !== outcome.key || before.count !== outcome.count) {
                    return outcome;
                }
            }

            return previous.value;
        }
    });
    // #endregion

    /**
     * The declared filter inputs of every column that has any, by column key. Recomputed only when the
     * columns or the filter state actually change, so the binding below keeps one identity across
     * change-detection passes rather than tripping development mode's `NG0100`.
     */
    private readonly columnFilterFieldsByKey = computed<ReadonlyMap<string, ShColumnFilterField[]>>(
        () =>
            new Map(
                (this.columns() ?? [])
                    .filter(column => column.columnDefinition.filterFields.length > 0)
                    .map(column => [
                        column.columnDefinition.key,
                        toColumnFilterFields(column, this.activeColumnFilters())
                    ])
            )
    );

    hasColumnFilter(column: Column<T>): boolean {
        return hasColumnFilter(column, this.enumFacetFieldKeys());
    }

    getSortColumnDef(column: Column<T>): SortableColumnDef<any, any> {
        return toSortColumnDef(column);
    }

    getColumnFilterValue(column: Column<T>): string {
        return getColumnFilterValue(column, this.activeColumnFilters());
    }

    getColumnFilterFields(column: Column<T>): ShColumnFilterField[] {
        return this.columnFilterFieldsByKey().get(column.columnDefinition.key) ?? NO_FILTER_FIELDS;
    }

    getEnumFacet(column: Column<T>): ShFacet | null {
        return getColumnEnumFacet(column, this.enumFacets());
    }

    getSelectedEnumValues(column: Column<T>): string[] {
        return getColumnSelectedEnumValues(column, this.activeFacetFilters());
    }

    onEnumFilterOpened(column: Column<T>): void {
        this.emitForDottedKey(column, this.enumFilterOpened);
    }

    onEnumFilterCleared(column: Column<T>): void {
        this.emitForDottedKey(column, this.enumFilterCleared);
    }

    /** The facet state is keyed by dotted key, not by the column key the text filters use. */
    private emitForDottedKey(column: Column<T>, target: { emit: (value: string) => void }): void {
        const dottedKey: string | undefined = column.fieldDefinition?.DottedKey;
        if (dottedKey) {
            target.emit(dottedKey);
        }
    }

    /**
     * Whether the sticky end / start columns are currently hiding content behind them. They carry an opaque
     * background, so without a shadow there is nothing to tell the user the table scrolls sideways at all —
     * and a shadow that is always on would claim there is more to see when there isn't.
     */
    readonly canScrollToEnd = signal(false);
    readonly isScrolledFromStart = signal(false);

    @ViewChild('scrollport')
    private readonly scrollport?: ElementRef<HTMLElement>;

    /** Observed alongside the scrollport: rows and columns change this element's size, not the scrollport's. */
    @ViewChild('grid')
    private readonly grid?: ElementRef<HTMLElement>;

    /** Null until the port has been measured once; the bar renders only once a selection exists anyway. */
    readonly selectionBarBounds = signal<SelectionBarBounds | null>(null);

    private resizeObserver?: ResizeObserver;
    private observedScrollport?: HTMLElement;
    private observedGrid?: HTMLElement;

    /**
     * Only ever re-wires the observer — the measuring happens in the observer's own callback (which fires
     * once on `observe()`) and on scroll, both outside change detection. Keeping the layout read out of this
     * hook is what makes it cheap enough to run on every check.
     */
    ngAfterViewChecked(): void {
        const scrollport: HTMLElement | undefined = this.scrollport?.nativeElement;
        const grid: HTMLElement | undefined = this.grid?.nativeElement;

        if (scrollport === this.observedScrollport && grid === this.observedGrid) {
            return;
        }

        this.observedScrollport = scrollport;
        this.observedGrid = grid;
        this.observeResize();
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    onScroll(event: Event): void {
        this.measure(event.target as HTMLElement);
    }

    private observeResize(): void {
        this.resizeObserver?.disconnect();

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        this.resizeObserver = new ResizeObserver(() => {
            if (this.observedScrollport) {
                this.measure(this.observedScrollport);
            }
        });

        for (const element of [this.observedScrollport, this.observedGrid]) {
            if (element) {
                this.resizeObserver.observe(element);
            }
        }
    }

    private measure(scrollport: HTMLElement): void {
        this.canScrollToEnd.set(scrollport.scrollLeft + scrollport.clientWidth < scrollport.scrollWidth - 1);
        this.isScrolledFromStart.set(scrollport.scrollLeft > 0);
        this.measureSelectionBar(scrollport);
    }

    /**
     * The selection bar is fixed, and a fixed box cannot be as wide as the content column it belongs to: its
     * percentages resolve against the window, and stretching it edge to edge would lay it over the navigation
     * and the filter panel. So it is given the port's own geometry, re-read whenever that changes — which the
     * observer above already covers, the filter panel's collapse included.
     */
    private measureSelectionBar(scrollport: HTMLElement): void {
        const rect: DOMRect = scrollport.getBoundingClientRect();
        const current: SelectionBarBounds | null = this.selectionBarBounds();

        if (current?.left === rect.left && current?.width === rect.width) {
            return;
        }

        this.selectionBarBounds.set({ left: rect.left, width: rect.width });
    }
}
